/* eslint-disable camelcase */
import * as React from 'react';

import styled from '@emotion/styled';
import {
  NoDataTd,
  Pager,
  Sortable,
  TableActions,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@lightspeed/cirrus-table';
import { Card } from '@lightspeed/flame/Card';
import { Box, Flex } from '@lightspeed/flame/Core';
import { IconHelp } from '@lightspeed/flame/Icon/Help';
import { Text } from '@lightspeed/flame/Text';
import { Tooltip } from '@lightspeed/flame/Tooltip';
import Moment from 'moment';

import { getSettingsLink } from '../helpers/getSettingsLink';
import { fetchReorderSuggestion } from '../helpers/ReorderSuggestionAPI';
import { ToolTipTexts } from '../helpers/TooltipTexts';
import { createReactRenderer } from 'helpers/react-renderer';

import { ReorderInput } from './ReorderInput';
import { ForecastThLight, HeaderWithForecasts, Table } from './ReorderPoints.style';
import { SuggestedCells } from './SuggestedCells';
import { useTableDataSet } from './use-table-data-set';

import type { ReorderSuggestion, Shop } from '../utils/types';

type Props = {
  itemID: number;
  shops: Shop[];
  hasAnalytics: boolean;
  readOnly: boolean;
};

const {
  NeedTooltipText,
  SuggestedLevelTooltipText,
  SuggestedNeedTooltipText,
  SuggestedPointTooltipText,
} = ToolTipTexts;

const StyledTooltip = styled(Tooltip)`
  width: 300px;
  font-weight: normal;
  z-index: 10;
  font-size: 12px;
  line-height: 18px;
  text-align: left;
`;

const ReorderPoints: React.FC<Props> = ({
  itemID,
  shops,
  hasAnalytics = false,
  readOnly = true,
}) => {
  const [shopsData, setShopsData] = React.useState(shops || []);
  const { rows, pagination, changePagination, getOrderDirection, setOrderDirection } =
    useTableDataSet(shopsData);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<Error>();
  const [lastSync, setLastSync] = React.useState('');
  const [browserLanguage, setBrowserLanguage] = React.useState(navigator.language || 'en');
  const showDuration = hasAnalytics && !isLoading && !loadError && lastSync;

  const getLocalizeLanguage = () => {
    if (window.Localize) {
      setBrowserLanguage(window.Localize.getLanguage());
    }
  };

  const mergeSuggestions = ({
    data,
    last_sync_time,
  }: {
    data: ReorderSuggestion[];
    last_sync_time: string;
  }) => {
    setLastSync(last_sync_time);
    setShopsData((prevShops) => {
      return prevShops.map((shop) => {
        const newShop = { ...shop };
        const suggestedShop = data.find((shop) => shop.shop_id === newShop.shop_id);
        if (suggestedShop) {
          newShop.dynamic_reorder_level = suggestedShop.dynamic_reorder_level;
          newShop.dynamic_reorder_point = suggestedShop.dynamic_reorder_point;

          newShop.dynamic_need = 0;
          if (
            Number.isInteger(newShop.dynamic_reorder_level) &&
            Number.isInteger(newShop.dynamic_reorder_point) &&
            newShop.total_qoh <= newShop.dynamic_reorder_point!
          ) {
            newShop.dynamic_need = newShop.dynamic_reorder_level! - newShop.total_qoh;
          }
        }

        return newShop;
      });
    });
    setIsLoading(false);
  };

  const updateReorderSuggestion = () => {
    if (!hasAnalytics) return;

    setIsLoading(true);
    fetchReorderSuggestion({
      itemID,
      setData: mergeSuggestions,
      setError: (error) => {
        setLoadError(error);
        setIsLoading(false);
      },
    });
  };

  React.useEffect(updateReorderSuggestion, []);

  React.useEffect(() => {
    getLocalizeLanguage();
  }, []);

  const Paginator = () => (
    <TableActions.ActionsBar
      rightHandActions={
        <Pager
          isFirst={pagination.isFirst}
          isLast={pagination.isLast}
          handlePrevious={() => changePagination('previous')}
          handleNext={() => changePagination('next')}
        >
          <span style={{ color: '#020203' }}>
            <var>
              <b>{pagination.rangeLabel}</b>
            </var>{' '}
            of <var>{shops.length}</var> locations
          </span>
        </Pager>
      }
    />
  );

  const TableHeader = ({
    sortKey,
    label,
    tooltipText,
    rightMost = false,
  }: {
    sortKey: string;
    label: string;
    tooltipText?: string;
    rightMost?: boolean;
  }) => {
    return (
      <Sortable.Th
        direction={getOrderDirection(sortKey)}
        onClick={() => setOrderDirection(sortKey)}
      >
        {label}
        {tooltipText && (
          <Box ml="1">
            <StyledTooltip content={tooltipText} placement={rightMost ? 'top-end' : 'top'}>
              <IconHelp baseColor="none" detailsColor1="#232429" detailsColor2="#232429" />
            </StyledTooltip>
          </Box>
        )}
      </Sortable.Th>
    );
  };

  return (
    <>
      <Text size="xlarge" fontWeight="700" mb={6} color="#020203">
        Reorder points
      </Text>
      <Card>
        <Paginator />
        <Table>
          <Thead>
            <Tr
              style={{
                borderBottom: 'solid 1px rgb(215, 220, 225)',
              }}
            >
              <Th />
              <Th colSpan={3} fontSize="1rem">
                Manual reordering
              </Th>
              <ForecastThLight colSpan={2} style={{ borderLeft: 'solid 1px #d7dce1' }}>
                <Flex flexDirection="row" alignItems="center">
                  <img
                    alt="Lightspeed logo"
                    width="17"
                    height="17"
                    src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/smalltile.png"
                    style={{ marginLeft: '-3px', marginRight: '6px' }}
                  />
                  <div>
                    <span
                      style={{
                        fontSize: '1rem',
                        paddingRight: '0.25rem',
                      }}
                    >
                      Demand forecasting{' '}
                    </span>
                    {showDuration && (
                      <span
                        style={{
                          display: 'inline-block',
                          fontWeight: 'normal',
                          fontSize: '12px',
                        }}
                      >
                        Last update <var>{Moment(lastSync).locale(browserLanguage).fromNow()}</var>
                      </span>
                    )}
                  </div>
                </Flex>
              </ForecastThLight>
              {hasAnalytics ? (
                <ForecastThLight style={{ textAlign: 'right' }}>
                  <a href={getSettingsLink()} rel="nofollow noreferrer noopener" target="_blank">
                    Edit settings
                  </a>
                </ForecastThLight>
              ) : (
                <ForecastThLight />
              )}
            </Tr>
            <HeaderWithForecasts>
              <TableHeader sortKey="name" label="Location" />
              <TableHeader sortKey="reorder_point" label="Point" />
              <TableHeader sortKey="reorder_level" label="Level" />
              <TableHeader sortKey="need" label="Need" tooltipText={NeedTooltipText} />
              <TableHeader
                sortKey="dynamic_reorder_point"
                label="Suggested point"
                tooltipText={SuggestedPointTooltipText}
              />
              <TableHeader
                sortKey="dynamic_reorder_level"
                label="Suggested level"
                tooltipText={SuggestedLevelTooltipText}
              />
              <TableHeader
                sortKey="dynamic_need"
                label="Suggested need"
                tooltipText={SuggestedNeedTooltipText}
                rightMost
              />
            </HeaderWithForecasts>
          </Thead>
          <Tbody>
            {rows.length ? (
              rows.map((shop, i) => (
                <Tr key={shop.shop_id}>
                  <Td className="notranslate">{shop.name}</Td>
                  <Td>
                    <ReorderInput type="pnt" shop={shop} readOnly={readOnly} />
                  </Td>
                  <Td>
                    <ReorderInput type="lvl" shop={shop} readOnly={readOnly} />
                  </Td>
                  <Td style={{ textAlign: 'right' }}>
                    <var>{shop.need}</var>
                  </Td>
                  <SuggestedCells
                    shop={shop}
                    index={i}
                    rowCount={rows.length}
                    hasAnalytics={hasAnalytics}
                    isLoading={isLoading}
                    isError={!!loadError}
                    onReload={updateReorderSuggestion}
                  />
                </Tr>
              ))
            ) : (
              <Tr>
                <NoDataTd colSpan={4}>
                  <Flex paddingY={6} width="100%" justifyContent="center" flexDirection="column">
                    <img
                      style={{ width: '250px', alignSelf: 'center' }}
                      alt="empty table"
                      src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-118c0e2/images/empty-table.svg"
                    />
                    <Text size="normal" fontWeight="700" paddingTop={4}>
                      No locations available for this item.
                    </Text>
                  </Flex>
                </NoDataTd>
              </Tr>
            )}
          </Tbody>
        </Table>
        <Paginator />
      </Card>
    </>
  );
};

const RenderedComponent = createReactRenderer({
  component: ReorderPoints,
  selector: '[data-react-root="ItemReorderPoints"]',
});

export default ReorderPoints;

document.addEventListener('DOMContentLoaded', () => {
  if (window.lsCloud.Modules.hasModule('analytics.inventory.integration')) {
    history.onChangeState(
      [
        {
          name: ['item.views.item'],
          form_name: 'view',
          id: undefined,
        },
      ],
      () => {
        RenderedComponent.mount();
      },
      () => {
        RenderedComponent.unmount();
      }
    );
  }
});
